import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Film, Calendar as CalendarIcon, ExternalLink, AlertCircle, Clock, CalendarClock, CalendarX } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import FilterChip from '../../components/crm/FilterChip';
import EmptyState from '../../components/crm/EmptyState';
import RescheduleDecisionDialog from '../../components/admin/RescheduleDecisionDialog';
import OverageDecisionDialog from '../../components/admin/OverageDecisionDialog';
import AssignShooterDialog from '../../components/admin/AssignShooterDialog';
import ClosuresManager from '../../components/admin/ClosuresManager';
import { UserPlus, List, LayoutGrid, ShieldCheck } from 'lucide-react';
import CalendarView from '../../components/media/MediaCalendar/MediaCalendar';
import { ApproveBookingModal, EventRequestModal } from '../../components/admin/MediaAdminOverrides';
import {
  PACKAGE_STATUSES,
  EVENT_STATUSES,
  packageStatusLabel,
  packageStatusBadgeClass,
  eventStatusLabel,
  eventStatusBadgeClass,
  noticeTierBadgeClass,
  noticeTierLabel,
  formatMediaDateRange,
  formatExpiryDate,
} from '../../lib/media';

// /admin/media — top-level media-package admin page.
//
// Two tabs:
//   Packages — every package sold across all accounts. Filterable by status.
//   Events   — every booking across all packages. Filterable by status +
//              quick "needs attention" toggle (pending past deadline,
//              overage awaiting review).
//
// Read-only in slice 1 (MP.1). Future slices add: create-package shortcut,
// per-event detail page, bulk actions.

export default function Media() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = ['events', 'reschedules', 'closures'].includes(searchParams.get('view'))
    ? searchParams.get('view')
    : 'packages';

  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') {
    return <EmptyState title="Staff only" description="Media is restricted to NexSav staff." />;
  }

  const setView = (next) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('view', next);
    sp.delete('status');
    sp.delete('needs_attention');
    setSearchParams(sp, { replace: false });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Media</span></h1>
          <p className="text-[13px] text-gray-500 mt-0.5">
            Event-coverage packages sold to schools, and the bookings against them.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-white/[0.06]">
        <TabButton active={view === 'packages'} onClick={() => setView('packages')}>
          <Film className="w-3.5 h-3.5" /> Packages
        </TabButton>
        <TabButton active={view === 'events'} onClick={() => setView('events')}>
          <CalendarIcon className="w-3.5 h-3.5" /> Events
        </TabButton>
        <TabButton active={view === 'reschedules'} onClick={() => setView('reschedules')}>
          <CalendarClock className="w-3.5 h-3.5" /> Reschedule requests
        </TabButton>
        <TabButton active={view === 'closures'} onClick={() => setView('closures')}>
          <CalendarX className="w-3.5 h-3.5" /> Closures
        </TabButton>
      </div>

      {view === 'packages' && <PackagesView searchParams={searchParams} setSearchParams={setSearchParams} />}
      {view === 'events' && <EventsView searchParams={searchParams} setSearchParams={setSearchParams} />}
      {view === 'reschedules' && <ReschedulesView />}
      {view === 'closures' && <ClosuresManager />}
    </div>
  );
}

function ReschedulesView() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [decideTarget, setDecideTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/api/admin/media/reschedule-requests?status=pending');
      setRequests(r?.requests || []);
    } catch (err) {
      setError(err?.message || 'Failed to load reschedule requests');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-[12px] text-gray-500">Loading…</div>;
  if (error) return <div className="text-[12px] text-red-300">{error}</div>;
  if (requests.length === 0) {
    return <EmptyState icon={CalendarClock} title="No pending requests" description="School-initiated reschedule requests appear here for Ops to approve or reject." />;
  }
  return (
    <>
      <div className="space-y-2">
        {requests.map((r) => (
          <button key={r.id} type="button" onClick={() => setDecideTarget(r)}
            className="w-full text-left rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-white font-medium truncate">{r.eventTitle}</span>
                  <span className="text-[11px] text-gray-500">{r.accountName}</span>
                </div>
                <div className="text-[11px] text-gray-500 mt-1">
                  Requested by {r.requestedByName} · {new Date(r.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </div>
              <div className="text-right shrink-0">
                {r.assignedUserName && <div className="text-[11px] text-gray-500">{r.assignedUserName}</div>}
              </div>
            </div>
          </button>
        ))}
      </div>
      {decideTarget && (
        <RescheduleDecisionDialog
          request={decideTarget}
          onClose={() => setDecideTarget(null)}
          onDecided={() => { setDecideTarget(null); load(); }}
        />
      )}
    </>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] border-b-2 -mb-px transition-colors ${
        active
          ? 'border-[#B0FC38] text-white'
          : 'border-transparent text-gray-500 hover:text-gray-300'
      }`}
    >
      {children}
    </button>
  );
}

function PackagesView({ searchParams, setSearchParams }) {
  const status = searchParams.get('status') || '';
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const setStatus = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (next) sp.set('status', next); else sp.delete('status');
    setSearchParams(sp, { replace: false });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sp = new URLSearchParams();
      if (status) sp.set('status', status);
      const res = await api.get(`/api/admin/media/packages?${sp.toString()}`);
      setPackages(res?.packages || []);
    } catch (err) {
      setError(err?.message || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterChip active={!status} onClick={() => setStatus('')}>All</FilterChip>
        {PACKAGE_STATUSES.map((s) => (
          <FilterChip key={s} active={status === s} onClick={() => setStatus(s)}>
            {packageStatusLabel(s)}
          </FilterChip>
        ))}
      </div>

      {error && (
        <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">
          {error}
        </div>
      )}

      {!loading && packages.length === 0 ? (
        <EmptyState
          icon={Film}
          title="No packages yet"
          description="Media packages appear here automatically when an invoice with a media-package line item is signed."
        />
      ) : (
        <div className="space-y-2">
          {packages.map((p) => <PackageListRow key={p.id} pkg={p} />)}
        </div>
      )}
    </>
  );
}

function PackageListRow({ pkg }) {
  return (
    <Link
      to={`/admin/accounts/${pkg.accountId}?tab=media`}
      className="block rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] p-4 transition-colors"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-white font-medium truncate">{pkg.accountName || pkg.accountId}</span>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${packageStatusBadgeClass(pkg.status)}`}>
              {packageStatusLabel(pkg.status)}
            </span>
            <span className="text-[11px] text-gray-500">{pkg.label}</span>
          </div>
          <div className="text-[11px] text-gray-500 mt-1">
            Purchased {formatExpiryDate(pkg.purchaseDate)} · Expires {formatExpiryDate(pkg.expiresOn)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Remaining</div>
          <div className="text-[13px]">
            <span className="font-semibold text-[#B0FC38]">{pkg.eventsRemaining}</span>
            <span className="text-gray-500"> / {pkg.eventsTotal}</span>
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            {pkg.eventsUsed} used · {pkg.eventsReserved} reserved
          </div>
        </div>
      </div>
    </Link>
  );
}

function EventsView({ searchParams, setSearchParams }) {
  const status = searchParams.get('status') || '';
  const needsAttention = searchParams.get('needs_attention') === '1';
  const unassigned = searchParams.get('unassigned') === '1';
  const approvalPending = searchParams.get('approval') === 'pending';
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overageTarget, setOverageTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const navigate = useNavigate();

  const onEventDrop = async ({ event, start, end }) => {
    try {
      await api.post(`/api/admin/media/events/${event.id}/force-reschedule`, {
        newScheduledStartAt: start.toISOString(),
        newScheduledEndAt: end.toISOString(),
        reason: 'Rescheduled via calendar drag',
      });
      load();
    } catch (err) {
      setError(err?.message || 'Reschedule failed');
    }
  };

  const setStatus = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (next) sp.set('status', next); else sp.delete('status');
    sp.delete('needs_attention');
    sp.delete('unassigned');
    sp.delete('approval');
    setSearchParams(sp, { replace: false });
  };

  const toggleApproval = () => {
    const sp = new URLSearchParams(searchParams);
    if (approvalPending) sp.delete('approval');
    else { sp.set('approval', 'pending'); sp.delete('status'); sp.delete('needs_attention'); sp.delete('unassigned'); }
    setSearchParams(sp, { replace: false });
  };

  const toggleNeedsAttention = () => {
    const sp = new URLSearchParams(searchParams);
    if (needsAttention) sp.delete('needs_attention');
    else { sp.set('needs_attention', '1'); sp.delete('status'); sp.delete('unassigned'); sp.delete('approval'); }
    setSearchParams(sp, { replace: false });
  };

  const toggleUnassigned = () => {
    const sp = new URLSearchParams(searchParams);
    if (unassigned) sp.delete('unassigned');
    else { sp.set('unassigned', '1'); sp.delete('status'); sp.delete('needs_attention'); sp.delete('approval'); }
    setSearchParams(sp, { replace: false });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sp = new URLSearchParams();
      if (status) sp.set('status', status);
      if (needsAttention) sp.set('needs_attention', '1');
      if (unassigned) sp.set('unassigned', '1');
      if (approvalPending) sp.set('approval', 'pending');
      const res = await api.get(`/api/admin/media/events?${sp.toString()}`);
      setEvents(res?.events || []);
    } catch (err) {
      setError(err?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [status, needsAttention, unassigned, approvalPending]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterChip active={!status && !needsAttention} onClick={() => setStatus('')}>All</FilterChip>
        {EVENT_STATUSES.map((s) => (
          <FilterChip key={s} active={status === s} onClick={() => setStatus(s)}>
            {eventStatusLabel(s)}
          </FilterChip>
        ))}
        <FilterChip active={approvalPending} onClick={toggleApproval}>
          <ShieldCheck className="inline w-3 h-3 mr-1" />
          Pending approval
        </FilterChip>
        <FilterChip
          active={needsAttention}
          onClick={toggleNeedsAttention}
        >
          <AlertCircle className="inline w-3 h-3 mr-1" />
          Needs attention
        </FilterChip>
        <FilterChip active={unassigned} onClick={toggleUnassigned}>
          <UserPlus className="inline w-3 h-3 mr-1" />
          Needs assignment
        </FilterChip>
        <div className="ml-auto inline-flex rounded-full bg-white/[0.04] border border-white/[0.08] p-0.5">
          <button type="button" onClick={() => setViewMode('list')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] ${viewMode === 'list' ? 'bg-[#B0FC38] text-gray-900' : 'text-gray-400 hover:text-white'}`}>
            <List className="w-3 h-3" /> List
          </button>
          <button type="button" onClick={() => setViewMode('calendar')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] ${viewMode === 'calendar' ? 'bg-[#B0FC38] text-gray-900' : 'text-gray-400 hover:text-white'}`}>
            <LayoutGrid className="w-3 h-3" /> Calendar
          </button>
        </div>
      </div>

      {error && (
        <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">
          {error}
        </div>
      )}

      {viewMode === 'calendar' ? (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
          <CalendarView
            events={events.map((e) => ({
              id: e.id, title: `${e.eventTitle}${e.accountName ? ' · ' + e.accountName : ''}`,
              start: e.scheduledStartAt, end: e.scheduledEndAt, status: e.status, accountId: e.accountId, raw: e,
            }))}
            draggable
            onEventDrop={onEventDrop}
            onSelectEvent={(ev) => setDetailTarget(ev.raw)}
          />
          <p className="text-[11px] text-gray-500 mt-2">Drag an event to reschedule it (admin force-reschedule). Click an event for full details.</p>
        </div>
      ) : !loading && events.length === 0 ? (
        <EmptyState
          icon={CalendarIcon}
          title="No events"
          description={status || needsAttention ? 'Try clearing filters.' : 'Bookings will appear here once schools start scheduling events.'}
        />
      ) : (
        <div className="space-y-1.5">
          {events.map((e) => (
            <EventListRow key={e.id} ev={e}
              onOpen={() => setDetailTarget(e)}
              onResolveOverage={() => setOverageTarget(e)}
              onAssign={() => setAssignTarget(e)}
              onApprove={() => setApproveTarget(e)} />
          ))}
        </div>
      )}

      {overageTarget && (
        <OverageDecisionDialog
          event={overageTarget}
          onClose={() => setOverageTarget(null)}
          onDecided={() => { setOverageTarget(null); load(); }}
        />
      )}
      {assignTarget && (
        <AssignShooterDialog
          event={assignTarget}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => { setAssignTarget(null); load(); }}
        />
      )}
      {approveTarget && (
        <ApproveBookingModal
          event={approveTarget}
          onChanged={() => { setApproveTarget(null); load(); }}
          onClose={() => setApproveTarget(null)}
        />
      )}
      {detailTarget && (
        <EventRequestModal
          event={detailTarget}
          onClose={() => setDetailTarget(null)}
          actions={[
            ...(detailTarget.approvalState === 'pending' && detailTarget.status === 'pending'
              ? [{ label: 'Approve / Reject', variant: 'primary', onClick: () => { setApproveTarget(detailTarget); setDetailTarget(null); } }]
              : []),
            ...(detailTarget.status === 'pending' && !detailTarget.assignedUserName && !detailTarget.externalFallbackName
              ? [{ label: 'Assign shooter', variant: 'primary', onClick: () => { setAssignTarget(detailTarget); setDetailTarget(null); } }]
              : []),
            ...(detailTarget.overageMinutes > 0 && !detailTarget.overageDecision
              ? [{ label: 'Resolve overage', onClick: () => { setOverageTarget(detailTarget); setDetailTarget(null); } }]
              : []),
            { label: 'View account', onClick: () => navigate(`/admin/accounts/${detailTarget.accountId}?tab=media`) },
          ]}
        />
      )}
    </>
  );
}

// Clicking the row opens the full request (the school's questionnaire); the
// trailing link icon still jumps to the account's Media tab.
function EventListRow({ ev, onOpen, onResolveOverage, onAssign, onApprove }) {
  const needsOverageReview = ev.overageMinutes && ev.overageMinutes > 30 && !ev.overageDecision;
  const needsApproval = ev.status === 'pending' && ev.approvalState === 'pending';
  const needsAssignment = !needsApproval && ev.status === 'pending' && !ev.assignedUserName && !ev.externalFallbackName;
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`media-event-row-${ev.id}`}
      onClick={() => onOpen?.()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(); } }}
      className="block rounded-lg border border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.04] px-3 py-2.5 transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] text-white truncate">{ev.eventTitle}</span>
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${eventStatusBadgeClass(ev.status)}`}>
              {eventStatusLabel(ev.status)}
            </span>
            {ev.noticeTier !== 'standard' && (
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${noticeTierBadgeClass(ev.noticeTier)}`}>
                {noticeTierLabel(ev.noticeTier)}
              </span>
            )}
            {ev.turnaround === 'rush' && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange-500/[0.1] border border-orange-400/30 text-orange-300">
                Rush turnaround
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{ev.accountName || ev.accountId}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <CalendarIcon className="w-3 h-3" />
              {formatMediaDateRange(ev.scheduledStartAt, ev.scheduledEndAt)}
            </span>
            {ev.assignedUserName && <span>· {ev.assignedUserName}</span>}
            {!ev.assignedUserName && ev.externalFallbackName && <span>· {ev.externalFallbackName} (ext.)</span>}
            {!ev.assignedUserName && !ev.externalFallbackName && (
              <span className="inline-flex items-center gap-1 text-amber-300">
                <AlertCircle className="w-3 h-3" /> Unassigned
              </span>
            )}
          </div>
        </div>
        {ev.status === 'pending' && ev.confirmationDeadlineAt && (
          <div className="text-[10px] text-amber-300 inline-flex items-center gap-1 shrink-0">
            <Clock className="w-3 h-3" />
            Awaiting confirmation
          </div>
        )}
        {needsApproval && (
          <button type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onApprove?.(); }}
            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-400/30 bg-amber-500/[0.08] text-amber-300 hover:bg-amber-500/[0.16] shrink-0">
            <ShieldCheck className="w-3 h-3" /> Approve
          </button>
        )}
        {needsAssignment && (
          <button type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAssign?.(); }}
            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#B0FC38]/30 bg-[#B0FC38]/[0.1] text-[#B0FC38] hover:bg-[#B0FC38]/[0.2] shrink-0">
            <UserPlus className="w-3 h-3" /> Assign
          </button>
        )}
        {needsOverageReview && (
          <button type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onResolveOverage?.(); }}
            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-400/30 bg-amber-500/[0.06] text-amber-300 hover:bg-amber-500/[0.12] shrink-0">
            <AlertCircle className="w-3 h-3" />
            {Math.round(ev.overageMinutes)} min overage · resolve
          </button>
        )}
        <Link
          to={`/admin/accounts/${ev.accountId}?tab=media`}
          onClick={(e) => e.stopPropagation()}
          title="Open account Media tab"
          className="shrink-0 mt-0.5 p-0.5 text-gray-600 hover:text-white"
        >
          <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
